import { PropsWithChildren } from 'react';
import { useTheme } from '@mui/material/styles';
import { useProductPricing } from '../../../../../hooks/selectPackage/useProductPricing';
import { PricingProduct } from '../../../../../store/features/products/types';
import CardWrapper, { CardWrapperProps } from '../shared/CardWrapper';
import FirstLevelCard from '../shared/FirstLevelCard';
import Typography from '../../../../../shared/library/Typography';
import ProductDescription from '../shared/ProductDescription';
import PriceInfo from '../../../../../shared/components/PriceInfo';
import ProductDiscounts from '../shared/ProductDiscounts';
import ProductTooltipInfoIcon from '../shared/TooltipInfoIcon';
import TitleWithInlineElement from '../shared/TitleWithInlineElement';
import VatMessage from '../shared/VatMessage';

export interface PrimaryProductCardProps
  extends Omit<CardWrapperProps, 'onClick'> {
  product: PricingProduct;
  isSecondLevelActive?: boolean;
}

export default function PrimaryProductCard(
  props: PropsWithChildren<PrimaryProductCardProps>,
) {
  const { product, isSecondLevelActive, children, ...cardWrapperProps } = props;
  const theme = useTheme();
  const { finalPrice, initialPrice, tieredSavingEndDate, vatValue, discounts } =
    useProductPricing(product);

  return (
    <CardWrapper {...cardWrapperProps}>
      <FirstLevelCard
        cardActive={isSecondLevelActive}
        date={
          product.productDate && (
            <Typography
              variant="caption1"
              neutralColorIndex={700}
              opacity={0.7}
              className="informa-payment-primary-product-card__date"
            >
              {product.productDate}
            </Typography>
          )
        }
        title={
          product.name && (
            <TitleWithInlineElement
              title={product.name}
              variant="subtitle1"
              className="informa-payment-primary-product-card__title"
            >
              {product.tooltipText && (
                <ProductTooltipInfoIcon text={product.tooltipText} />
              )}
            </TitleWithInlineElement>
          )
        }
        description={
          product.description && (
            <Typography
              variant="smallText"
              component="div"
              neutralColorIndex={700}
              className="informa-payment-primary-product-card__description"
            >
              <ProductDescription description={product.description} />
            </Typography>
          )
        }
        price={
          <PriceInfo
            price={finalPrice}
            startPrice={initialPrice}
            discountDetails={
              <ProductDiscounts
                name={product.name}
                finalPrice={finalPrice}
                initialPrice={initialPrice}
                discounts={discounts}
                tieredSavingEndDate={tieredSavingEndDate}
                vat={<VatMessage vatAmount={vatValue} />}
              />
            }
            vat={<VatMessage vatAmount={vatValue} shortFormat />}
            variant={isSecondLevelActive ? 'featuredText' : 'h3'}
            className="informa-payment-primary-product-card__price"
            boxWrapperProps={{
              sx: {
                [theme.breakpoints.down('md')]: {
                  alignItems: 'start',
                },
              },
            }}
          />
        }
        actionButton={children}
        className="informa-payment-primary-product-card"
      />
    </CardWrapper>
  );
}
